import React, { useEffect, useState } from "react";
import { Button, Segmented, notification, Skeleton } from "antd";
import { useParams } from "react-router-dom";
import expandSVG from "../../assets/Illustrations/expandBtn.svg";
import collapseSVG from "../../assets/Illustrations/collapseBtn.svg";
import createNoteSVG from "../../assets/Illustrations/addNote.svg";
import ListNoteSVG from "../../assets/Illustrations/listNote.svg";

import WebUtils from "../../utils/WebUtils";
import {
  addNoteApi,
  addFreeGuideNoteApi,
  deleteNoteApi,
  deleteFreeGuideNoteApi,
  getNoteApi,
  getFreeGuidesNoteApi,
} from "../../api/candidate/index";
import Utils from "../../utils/Utils";

const userDetail = Utils.getUserDetail();

function Notes({
  guideDetails,
  currTopicId,
  currSubTopicId,
  handleViewJd,
  industryId,
}) {
  const [isCollapsed, setIsCollapsed] = useState(false); // Collapse state
  const [segmentedValue, setSegmentedValue] = useState("View all notes"); // Default to "Create new"
  const [note, setNote] = useState(""); // State for the note text

  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const [notes, setNotes] = useState([]); // State for storing fetched notes
  const [selectedNote, setSelectedNote] = useState(null); // State for the selected note

  const { jobGuideIdentifier } = useParams(); // Extract jobGuideIdentifier from URL

  const [expandedNotes, setExpandedNotes] = useState([]);
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    if (currTopicId && currSubTopicId) {
      fetchNotes();
    }
  }, [currTopicId, currSubTopicId]);

  const fetchNotes = () => {
    const userDetail = Utils.getUserDetail();
    let url = "";
    if (industryId) {
      url = `${getFreeGuidesNoteApi}?userIdentifier=${userDetail.userIdentifier}&jobGuideIdentifier=${jobGuideIdentifier}&industryId=${industryId}&topicIdentifier=${currTopicId}&subTopicIdentifier=${currSubTopicId}`;
    } else {
      url = `${getNoteApi}?userIdentifier=${userDetail.userIdentifier}&jobGuideIdentifier=${jobGuideIdentifier}&topicIdentifier=${currTopicId}&subTopicIdentifier=${currSubTopicId}`;
    }

    setSkeletonLoading(true);
    WebUtils.httpOperations(url, {}, "GET")
      .then((result) => {
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          const fetchedNotes = result.data.data?.jobGuideNotes || []; // Ensure it defaults to an empty array if undefined
          setNotes(fetchedNotes);
          setSkeletonLoading(false);
        } else {
          setSkeletonLoading(false);
        }
      })
      .catch((err) => {
        setSkeletonLoading(false);
        console.error(err);
      });
  };
  const handleAddNote = () => {
    if (!note) {
      notification.error({
        message: "Error",
        description: "Please type a note before submitting",
        placement: "topRight",
      });
      return;
    }

    let url = "";
    if (industryId) {
      url = `${addFreeGuideNoteApi}?industryId=${industryId}`;
    } else {
      url = addNoteApi;
    }

    const encodedNote = btoa(note); // Encode the note in base64
    const requestBody = {
      userIdentifier: userDetail?.userIdentifier,
      jobGuideIdentifier,
      topicIdentifier: currTopicId,
      subTopicIdentifier: currSubTopicId,
      note: encodedNote,
    };

    setSkeletonLoading(true);

    WebUtils.httpOperations(url, requestBody, "POST")
      .then((result) => {
        setSkeletonLoading(false);
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          notification.success({
            message: "Success",
            description: "Note added successfully!",
            placement: "topRight",
          });
          // Optionally clear the textarea after success
          setNote("");
          fetchNotes(); // Refresh the notes after adding a new one
        } else {
          notification.error({
            message: "Failed",
            description: "Failed to add note!",
            placement: "topRight",
          });
        }
      })
      .catch((err) => {
        setSkeletonLoading(false);
        console.error(err);
        notification.error({
          message: "Error",
          description: "An error occurred while adding the note",
          placement: "topRight",
        });
      });
  };
  const handleDeleteNote = (note) => {
    setSkeletonLoading(true);
    let url = "";
    if (industryId) {
      url = `${deleteFreeGuideNoteApi}?userIdentifier=${
        userDetail?.userIdentifier || null
      }&jobGuideIdentifier=${jobGuideIdentifier}&industryId=${industryId}&notesTopicIdentifier=${
        note?.topicId
      }&notesSubTopicIdentifier=${note?.subtopicId}`;
    } else {
      url = `${deleteNoteApi}?userIdentifier=${
        userDetail?.userIdentifier || null
      }&jobGuideIdentifier=${jobGuideIdentifier}&notesTopicIdentifier=${
        note?.topicId
      }&notesSubTopicIdentifier=${note?.subtopicId}`;
    }

    WebUtils.httpOperations(url, {}, "DELETE")
      .then((result) => {
        setSkeletonLoading(false);
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          notification.success({
            message: "Success",
            description: "Note deleted successfully!",
            placement: "topRight",
          });
          setNote("");
          fetchNotes();
        } else {
          notification.error({
            message: "Failed",
            description: "Failed to delete note!",
            placement: "topRight",
          });
        }
      })
      .catch((err) => {
        setSkeletonLoading(false);
        console.error(err);
        notification.error({
          message: "Error",
          description: "An error occurred while adding the note",
          placement: "topRight",
        });
      });
  };

  const handleToggleNote = (noteItem) => {
    if (expandedNotes.includes(noteItem.textNote)) {
      // If the note is already expanded, remove it from the list
      setExpandedNotes(
        expandedNotes.filter((note) => note !== noteItem.textNote)
      );
    } else {
      // If the note is not expanded, add it to the list
      setExpandedNotes([...expandedNotes, noteItem.textNote]);
    }
  };

  const truncatedText = (note) =>
    note.length > 74 ? note.slice(0, 74) + "..." : note;
  return (
    <>
      {skeletonLoading ? (
        <div className="">
          <Skeleton active className="" />
          <Skeleton active className="" />
          <Skeleton active className="" />
        </div>
      ) : (
        <div
          className={`driver-single-guide-user-notes-section relative bg-white 
           overflow-y-auto max-w-[660px] px-1 orange-scroll h-full`}
        >
          <>
            <div className="mb-[0.8rem] notes-segment">
              <Segmented
                options={["View all notes", "Create new"]}
                value={segmentedValue}
                onChange={setSegmentedValue}
                block
              />
            </div>

            {segmentedValue === "View all notes" ? (
              <>
                {!selectedNote ? (
                  <>
                    {/* <div className="mb-2 flex justify-between">
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item onClick={() => sortNotes("newest")}>
                          Newest first
                        </Menu.Item>
                        <Menu.Item onClick={() => sortNotes("oldest")}>
                          Oldest first
                        </Menu.Item>
                      </Menu>
                    }
                  >
                    <Button>Sort by</Button>
                  </Dropdown>
                </div> */}
                    <div className="h-[70%]">
                      {" "}
                      {/* Fixed height */}
                      {notes.length === 0 ? (
                        <div className="w-full h-full flex flex-col justify-center gap-[0.58rem] items-center px-3">
                          <div className="">
                            <img
                              src={ListNoteSVG}
                              alt="No notes"
                              className="max-w-[138px] mx-auto mt-2"
                            />
                          </div>
                          <div className="text-[#667085] text-[0.9rem] text-center leading-5">
                            No previous records found. Start fresh by creating a
                            new note!
                          </div>
                          <button
                            className="w-[120px] btn-orange px-3 py-[0.3rem] text-[.85rem]"
                            onClick={() => setSegmentedValue("Create new")}
                          >
                            Add note
                          </button>
                        </div>
                      ) : (
                        notes?.map((noteItem, index) => (
                          <div
                            key={index}
                            className={`border border-[#b8babc] rounded-lg py-[0.33rem] px-[0.6rem] mb-[0.6rem] break-words shadow-sm text-[#101828] transition-all duration-200 hover:border-[#206DC5]`}
                            // onClick={() => handleNoteClick(noteItem)}
                            // onClick={() => setSelectedNote(noteItem)}
                          >
                            {
                              expandedNotes.includes(noteItem.textNote)
                                ? noteItem.textNote // Full text if expanded
                                : truncatedText(noteItem.textNote) // Truncated text otherwise
                            }
                            {noteItem.textNote.length > 74 && (
                              <button
                                onClick={() => handleToggleNote(noteItem)}
                                className="ml-[0.3rem] text-[#206DC5] text-[0.94rem] font-semibold cursor-pointer"
                              >
                                {expandedNotes.includes(noteItem.textNote)
                                  ? "See less"
                                  : "See more"}
                              </button>
                            )}
                            <div
                              className="ml-auto w-[22px] text-red-400 cursor-pointer"
                              onClick={() => handleDeleteNote(noteItem)}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 32 32"
                                id="delete"
                                fill="red"
                                width="23"
                                height="23"
                              >
                                <path d="M24.2,12.193,23.8,24.3a3.988,3.988,0,0,1-4,3.857H12.2a3.988,3.988,0,0,1-4-3.853L7.8,12.193a1,1,0,0,1,2-.066l.4,12.11a2,2,0,0,0,2,1.923h7.6a2,2,0,0,0,2-1.927l.4-12.106a1,1,0,0,1,2,.066Zm1.323-4.029a1,1,0,0,1-1,1H7.478a1,1,0,0,1,0-2h3.1a1.276,1.276,0,0,0,1.273-1.148,2.991,2.991,0,0,1,2.984-2.694h2.33a2.991,2.991,0,0,1,2.984,2.694,1.276,1.276,0,0,0,1.273,1.148h3.1A1,1,0,0,1,25.522,8.164Zm-11.936-1h4.828a3.3,3.3,0,0,1-.255-.944,1,1,0,0,0-.994-.9h-2.33a1,1,0,0,0-.994.9A3.3,3.3,0,0,1,13.586,7.164Zm1.007,15.151V13.8a1,1,0,0,0-2,0v8.519a1,1,0,0,0,2,0Zm4.814,0V13.8a1,1,0,0,0-2,0v8.519a1,1,0,0,0,2,0Z"></path>
                              </svg>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <div className="p-4 border border-gray-300 rounded-lg bg-gray-50">
                    <Button onClick={() => setSelectedNote(null)}>
                      Back to notes
                    </Button>
                    <h4 className="text-md font-bold mb-2 mt-2">
                      Note Details
                    </h4>
                    <p>{selectedNote.textNote}</p>
                    <p className="text-gray-500 text-sm">
                      Created on:{" "}
                      {new Date(selectedNote.creationDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex flex-col gap-[1.1rem] items-center justify-center h-[74%]">
                  {/* <img
                src={getIllustration()}
                alt="Note illustration"
                className="w-[130px] h-[130px] mb-4"
              /> */}
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Type your thoughts..."
                    className="w-full h-full resize-none border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#206DC5]"
                    // rows="7"
                  />
                  <button
                    className="w-full btn-orange px-3 py-[0.4rem] text-[.9rem]"
                    onClick={handleAddNote}
                  >
                    Add note
                  </button>
                </div>
              </>
            )}
          </>
        </div>
      )}
    </>
  );
}

export default Notes;
